import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { TrendingUp, Warning, TrendingDown } from "@mui/icons-material";

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const growthRateValue = Number.parseFloat(data.growthRate || 0);

    return (
      <div
        style={{
          backgroundColor: "white",
          padding: "12px",
          border: "1px solid #e0e0e0",
          borderRadius: "4px",
          boxShadow: "0 3px 8px rgba(0,0,0,0.15)",
        }}
      >
        <h5
          style={{
            fontWeight: 600,
            marginTop: 0,
            marginBottom: "8px",
            color: "#333",
            borderBottom: "1px solid #eee",
            paddingBottom: "6px",
          }}
        >
          Fiscal Year {label}
        </h5>
        <div style={{ fontSize: "13px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "6px",
            }}
          >
            <span style={{ color: "#666", marginRight: "24px" }}>
              Total Paid:
            </span>
            <span style={{ fontWeight: 600, color: "#015198" }}>
              RWF {data.formattedTotal}
            </span>
          </div>

          {data.yearOverYearChange !== null && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "6px",
              }}
            >
              <span style={{ color: "#666", marginRight: "24px" }}>
                Year-over-Year Change:
              </span>
              <span
                style={{
                  fontWeight: 600,
                  color: data.yearOverYearChange > 0 ? "#2e7d32" : "#d32f2f",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {data.yearOverYearChange > 0 ? (
                  <TrendingUp style={{ fontSize: 14, marginRight: "4px" }} />
                ) : (
                  <TrendingDown style={{ fontSize: 14, marginRight: "4px" }} />
                )}
                {data.yearOverYearChange > 0 ? "+" : ""}
                RWF {data.formattedYearOverYearChange}
              </span>
            </div>
          )}

          {data.annotation && (
            <div
              style={{
                marginTop: "8px",
                paddingTop: "6px",
                borderTop: "1px solid #eee",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Warning
                style={{
                  height: "14px",
                  width: "14px",
                  marginRight: "6px",
                  color: "#ed6c02",
                }}
              />
              <span style={{ color: "#ed6c02", fontWeight: 500 }}>
                {data.annotation}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }
  return null;
};

export default function SelectedDistrictSalariesByFiscalYearChart({ data }) {
  const formattedData = data.map((item) => ({
    ...item,
    formattedTotal: new Intl.NumberFormat("en-US").format(item.totalPaid),
    totalInBillions: (item.totalPaid / 1000000000).toFixed(2),
  }));

  const firstYearLabel =
    formattedData.length > 0 ? formattedData[0].fiscalYear : "";
  const firstYearTotal =
    formattedData.length > 0 ? formattedData[0].totalPaid : 0;

  const enhancedData = formattedData.map((item, index) => {
    if (index === 0)
      return {
        ...item,
        growthRate: null,
        yearOverYearChange: null,
        formattedYearOverYearChange: null,
        annotation: "Baseline Year",
        firstYearLabel,
      };

    const previousYearTotal = formattedData[index - 1].totalPaid;
    const yearOverYearChange = item.totalPaid - previousYearTotal;
    const formattedYearOverYearChange = new Intl.NumberFormat("en-US").format(Math.abs(yearOverYearChange));

    const isPeakYear =
      item.totalPaid === Math.max(...formattedData.map((d) => d.totalPaid));
    const isCurrentYear = index === formattedData.length - 1;

    let annotation = null;
    if (
      index === formattedData.length - 2 &&
      isCurrentYear === false &&
      isPeakYear
    ) {
      annotation = "Peak expenditure";
    } else if (isCurrentYear) {
      annotation = "Current year";
    }

    return {
      ...item,
      growthRate: item.totalPaid,
      yearOverYearChange,
      formattedYearOverYearChange,
      annotation,
      firstYearLabel,
    };
  });

  const formatToBillions = (value) => {
    return `RWF ${(value / 1000000000).toFixed(2)}B`;
  };

  return (
    <div className="w-100">
      <ResponsiveContainer width="100%" height={500}>
        <ComposedChart
          data={enhancedData}
          margin={{ top: 20, right: 5, left: 20, bottom: 20 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            horizontal={false}
            vertical={false}
          />
          <XAxis
            dataKey="fiscalYear"
            interval={0}
            angle={-45}
            textAnchor="end"
            height={60}
            tick={{ fontSize: 15 }}
            padding={{ left: 10, right: 10 }}
          />
          <YAxis
            yAxisId="left"
            tickFormatter={formatToBillions}
            domain={[0, (dataMax) => Math.max(dataMax * 12, 1000000000)]}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            domain={[0, (dataMax) => Math.max(dataMax * 25, 10)]}
            tickFormatter={(value) => `RWF ${new Intl.NumberFormat("en-US").format(Math.abs(value / 1000000000))}B`}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar
            yAxisId="left"
            dataKey="totalPaid"
            name="Total Paid"
            barSize={60}
            fill="hsl(var(--chart-1))"
            radius={[4, 4, 0, 0]}
          ></Bar>
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="growthRate"
            name="Year-over-Year Change"
            stroke="hsl(var(--chart-2))"
            strokeWidth={2}
            dot={{ r: 4, strokeWidth: 2, fill: "white" }}
            activeDot={{ r: 6, strokeWidth: 2, stroke: "white" }}
            connectNulls
          />
        </ComposedChart>
      </ResponsiveContainer>
      <div
        style={{
          marginBottom: "20px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          style={{
            fontSize: "13px",
            color: "#666",
            display: "flex",
            alignItems: "center",
          }}
        >
          <span
            style={{
              height: "10px",
              width: "10px",
              backgroundColor: "hsl(var(--chart-1))",
              display: "inline-block",
              marginRight: "5px",
              borderRadius: "2px",
            }}
          ></span>
          <span style={{ marginRight: "15px" }}>
            Total Paid (RWF, billions)
          </span>

          <span
            style={{
              height: "10px",
              width: "10px",
              backgroundColor: "hsl(var(--chart-2))",
              display: "inline-block",
              marginRight: "5px",
              borderRadius: "2px",
            }}
          ></span>
          <span>Year-over-Year Change (RWF, billions)</span>
        </div>
      </div>
    </div>
  );
}
